import { CloseOutlined, MessageOutlined, SendOutlined } from '@ant-design/icons';
import { Avatar, Badge, Button, Input, List, Spin, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { ChatMessage, useChat } from '../hooks/useChat';
import { useI18n } from '../i18n';

const { TextArea } = Input;
const { Text } = Typography;

interface ChatWidgetProps {
  className?: string;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ className }) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { isAuthenticated, user } = useAuth();
  const { messages, loading, error, sendMessage, markAsRead, unreadCount } = useChat();

  // 自动滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 打开聊天时标记消息为已读
  useEffect(() => {
    if (isOpen) {
      markAsRead();
    }
  }, [isOpen, markAsRead]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    await sendMessage(inputValue);
    setInputValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const locale = localStorage.getItem('locale') || 'zh-TW';
    return date.toLocaleTimeString(locale === 'zh-TW' ? 'zh-TW' : locale === 'en' ? 'en-US' : 'zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMessageContent = (content: string) => {
    // 简单的换行处理
    return content.split('\n').map((line, index) => (
      <div key={index}>{line}</div>
    ));
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <style>
        {`
          .chat-widget .ant-spin-container {
            width: 100% !important;
            display: block !important;
          }
          .chat-widget .ant-list-items {
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .chat-widget .ant-list-item {
            width: 100% !important;
          }
        `}
      </style>
      <div className={`chat-widget ${className || ''}`}>
        {/* 聊天按钮 */}
        <Badge count={unreadCount} size="small">
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={<MessageOutlined />}
            onClick={() => setIsOpen(!isOpen)}
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              zIndex: 1000,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}
          />
        </Badge>

        {/* 聊天窗口 */}
        {isOpen && (
          <div
            className={`chat-window ${isMinimized ? 'minimized' : ''}`}
            style={{
              position: 'fixed',
              bottom: '80px',
              right: '20px',
              width: '350px',
              height: isMinimized ? '50px' : '500px',
              backgroundColor: 'white',
              border: '1px solid #d9d9d9',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* 聊天窗口头部 */}
            <div
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#fafafa',
                borderRadius: '8px 8px 0 0'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <MessageOutlined style={{ marginRight: '8px' }} />
                <Text strong>{t('customerService.chatWidget.title')}</Text>
              </div>
              <div>
                <Button
                  type="text"
                  size="small"
                  onClick={() => setIsMinimized(!isMinimized)}
                  style={{ marginRight: '4px' }}
                >
                  {isMinimized ? t('customerService.chatWidget.expand') : t('customerService.chatWidget.minimize')}
                </Button>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => setIsOpen(false)}
                />
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* 消息列表 */}
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '16px 0',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <Spin spinning={loading} style={{ width: '100%', display: 'block' }}>
                    {messages.length === 0 ? (
                      <div style={{ textAlign: 'center', color: '#999', marginTop: '50px' }}>
                        <MessageOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                        <div>{t('customerService.chatWidget.noMessages')}</div>
                      </div>
                    ) : (
                      <List
                        style={{
                          width: '100%',
                          padding: 0,
                          margin: 0
                        }}
                        split={false}
                        dataSource={messages}
                        renderItem={(msg: ChatMessage) => {
                          // 判断是否为用户的消息
                          const isUserMessage = msg.senderType?.toUpperCase() === 'USER';
                          return (
                            <List.Item
                              id='test-id'
                              style={{
                                padding: '8px 0',
                                border: 'none',
                                width: '100%',
                                display: 'block'
                              }}
                            >
                              {isUserMessage ? (
                                <div
                                  style={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'flex-end',
                                    alignItems: 'flex-start',
                                    paddingRight: '12px'
                                  }}
                                >
                                  <div
                                    style={{
                                      maxWidth: '70%',
                                      backgroundColor: '#1890ff',
                                      color: 'white',
                                      padding: '8px 12px',
                                      borderRadius: '12px',
                                      fontSize: '14px',
                                      lineHeight: '1.4'
                                    }}
                                  >
                                    {formatMessageContent(msg.content)}
                                    <div
                                      style={{
                                        fontSize: '11px',
                                        opacity: 0.7,
                                        marginTop: '4px'
                                      }}
                                    >
                                      {formatTime(msg.createdAt)}
                                    </div>
                                  </div>
                                  <Avatar
                                    style={{
                                      backgroundColor: '#1890ff',
                                      marginLeft: '8px',
                                      flexShrink: 0
                                    }}
                                    size="small"
                                  >
                                    {t('customerService.chatWidget.userAvatar')}
                                  </Avatar>
                                </div>
                              ) : (
                                <div
                                  style={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'flex-start',
                                    alignItems: 'flex-start',
                                    paddingLeft: '12px'
                                  }}
                                >
                                  <Avatar
                                    style={{
                                      backgroundColor: '#52c41a',
                                      marginRight: '8px',
                                      flexShrink: 0
                                    }}
                                    size="small"
                                  >
                                    {t('customerService.chatWidget.staffAvatar')}
                                  </Avatar>
                                  <div
                                    style={{
                                      maxWidth: '70%',
                                      backgroundColor: 'white',
                                      color: '#333',
                                      padding: '8px 12px',
                                      borderRadius: '12px',
                                      fontSize: '14px',
                                      lineHeight: '1.4'
                                    }}
                                  >
                                    {formatMessageContent(msg.content)}
                                    <div
                                      style={{
                                        fontSize: '11px',
                                        opacity: 0.7,
                                        marginTop: '4px'
                                      }}
                                    >
                                      {formatTime(msg.createdAt)}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </List.Item>
                          );
                        }}
                      />
                    )}
                    <div ref={messagesEndRef} />
                  </Spin>
                </div>

                {/* 输入框 */}
                <div
                  style={{
                    padding: '12px',
                    borderTop: '1px solid #f0f0f0',
                    backgroundColor: 'white',
                    borderRadius: '0 0 8px 8px'
                  }}
                >
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <TextArea
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={t('customerService.chatWidget.inputPlaceholder')}
                      autoSize={{ minRows: 1, maxRows: 3 }}
                      style={{ flex: 1 }}
                    />
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim()}
                    />
                  </div>
                  {error && (
                    <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                      {error}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <style>{`
        .chat-widget .chat-window {
          transition: height 0.3s ease;
        }
        
        .chat-widget .chat-window.minimized {
          height: 50px !important;
        }
      `}</style>
      </div>
    </>
  );
};

export default ChatWidget;
